import { useMemo, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { ConfirmDialog } from "@/components/base/ConfirmDialog";
import { IntegrationsNav } from "@/components/feature/IntegrationsNav";
import { providerMeta, marketplaceSiteLabel } from "@/lib/integrations/registry";
import { firstRelation } from "@/lib/relations";

interface Allocation {
  id: string;
  product_id: string;
  connector_id: string;
  channel_qty: number;
  safety_stock: number;
  reserved_qty: number;
  products?: { name: string; sku: string } | null;
  connectors?: { name: string; type: string; provider: string | null; marketplace_site: string | null } | null;
}

interface ProductOption { id: string; name: string; sku: string; }
interface ConnectorOption { id: string; name: string; type: string; provider: string | null; marketplace_site: string | null; }

interface BalanceRow { product_id: string; available: number; }

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export default function ChannelAllocations() {
  const { org } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Allocation | null>(null);
  const [form, setForm] = useState({ product_id: "", connector_id: "", channel_qty: "", safety_stock: "" });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["channel-allocations", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const [allocations, products, connectors, balances] = await Promise.all([
        supabase.from("channel_allocations").select("id, product_id, connector_id, channel_qty, safety_stock, reserved_qty, products(name, sku), connectors(name, type)").eq("org_id", org!.id).order("created_at", { ascending: false }),
        supabase.from("products").select("id, name, sku").eq("org_id", org!.id).order("name"),
        supabase.from("connectors").select("id, name, type, provider, marketplace_site").eq("org_id", org!.id).eq("enabled", true).order("name"),
        supabase.from("inventory_balances").select("product_id, available").eq("org_id", org!.id),
      ]);
      return {
        allocations: (allocations.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
          connectors: firstRelation(row.connectors),
        })) as Allocation[],
        products: (products.data ?? []) as ProductOption[],
        connectors: (connectors.data ?? []) as ConnectorOption[],
        balances: (balances.data ?? []) as BalanceRow[],
      };
    },
  });

  const availableByProduct = useMemo(() => {
    const map: Record<string, number> = {};
    for (const b of data?.balances ?? []) {
      map[b.product_id] = (map[b.product_id] ?? 0) + Number(b.available ?? 0);
    }
    return map;
  }, [data]);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    const qty = Number(form.channel_qty);
    if (!form.product_id || !form.connector_id || !form.channel_qty || Number.isNaN(qty) || qty <= 0) {
      setError("Pick a product, a channel, and a positive quantity.");
      return;
    }
    setSaving(true);
    const { error: insErr } = await supabase.from("channel_allocations").insert({
      org_id: org!.id,
      product_id: form.product_id,
      connector_id: form.connector_id,
      channel_qty: qty,
      safety_stock: form.safety_stock ? Number(form.safety_stock) : 0,
      reserved_qty: 0,
    });
    setSaving(false);
    if (insErr) {
      setError(insErr.message);
      return;
    }
    setOpen(false);
    setForm({ product_id: "", connector_id: "", channel_qty: "", safety_stock: "" });
    queryClient.invalidateQueries({ queryKey: ["channel-allocations", org?.id] });
  };

  const removeAllocation = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    await supabase.from("channel_allocations").delete().eq("id", deleteTarget.id);
    setSaving(false);
    setDeleteTarget(null);
    queryClient.invalidateQueries({ queryKey: ["channel-allocations", org?.id] });
  };

  const connectorLabel = (c?: { name: string; provider: string | null; marketplace_site: string | null } | null) => {
    if (!c) return "—";
    const meta = providerMeta(c.provider);
    const site = marketplaceSiteLabel(c.marketplace_site);
    return [c.name, meta?.label, site].filter(Boolean).join(" · ");
  };

  return (
    <div>
      <PageHeader title="Channel Allocations" subtitle="Publish sellable inventory to each sales channel">
        <button
          onClick={() => { setError(""); setOpen(true); }}
          disabled={!data || data.connectors.length === 0}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-add-line" /> New Allocation
        </button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {isLoading ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8"><LoadingState /></div>
          ) : isError ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <p className="text-sm text-slate-500 mb-4">Couldn't load allocations.</p>
              <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
            </div>
          ) : !data || data.allocations.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200">
              <EmptyState icon="ri-store-2-line" title="No channel allocations" description="Allocate stock to a sales channel to control how much is published there." />
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50/70 border-b border-slate-100">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Channel</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Channel qty</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Safety stock</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Available</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.allocations.map((a) => {
                      const available = availableByProduct[a.product_id] ?? 0;
                      const over = available < a.channel_qty;
                      return (
                        <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-slate-800">{a.products?.name ?? "—"}</p>
                            <p className="text-xs text-slate-400 font-mono">{a.products?.sku}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-700">{connectorLabel(a.connectors)}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-right font-medium">{a.channel_qty}</td>
                          <td className="px-4 py-3 text-sm text-slate-600 text-right">{a.safety_stock}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`inline-flex items-center gap-1 text-sm font-semibold ${over ? "text-amber-600" : "text-emerald-700"}`}>
                              {available}
                              {over && <i className="ri-alert-line text-xs" title="Allocation exceeds available stock" />}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button onClick={() => setDeleteTarget(a)} className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500">
                              <i className="ri-delete-bin-line" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <IntegrationsNav />
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">How this works</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Your inventory ledger remains the single source of truth. Channel allocations control how much of your
              <span className="font-medium text-slate-700"> available</span> stock is published to each connector —
              with a safety buffer kept back from selling. Connectors read from here, they never write to your core stock.
            </p>
          </div>
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New channel allocation"
        footer={
          <>
            <button onClick={() => setOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleCreate} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Saving..." : "Save Allocation"}
            </button>
          </>
        }
      >
        <form onSubmit={handleCreate} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div>
            <label className={labelCls}>Product *</label>
            <select required value={form.product_id} onChange={(e) => setForm((f) => ({ ...f, product_id: e.target.value }))} className={inputCls}>
              <option value="">Select product...</option>
              {data?.products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Channel *</label>
            <select required value={form.connector_id} onChange={(e) => setForm((f) => ({ ...f, connector_id: e.target.value }))} className={inputCls}>
              <option value="">Select channel...</option>
              {data?.connectors.map((c) => <option key={c.id} value={c.id}>{connectorLabel(c)}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Channel quantity *</label>
              <input type="number" min="1" value={form.channel_qty} onChange={(e) => setForm((f) => ({ ...f, channel_qty: e.target.value }))} className={inputCls} placeholder="10" />
            </div>
            <div>
              <label className={labelCls}>Safety stock</label>
              <input type="number" min="0" value={form.safety_stock} onChange={(e) => setForm((f) => ({ ...f, safety_stock: e.target.value }))} className={inputCls} placeholder="2" />
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Remove allocation"
        message={`Remove the allocation for "${deleteTarget?.products?.name ?? ""}" on ${deleteTarget?.connectors?.name ?? ""}?`}
        confirmLabel="Remove"
        loading={saving}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={removeAllocation}
      />
    </div>
  );
}
